import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchStockStatus } from '@theme/bbd-stock-status-v2';

const SEVERITIES = ['instock', 'outofstock'];

/**
 * Online stock status line on the PDP, driven by the v2 stock status API.
 */
class BbdOnlineStockStatus extends Component {
  requiredRefs = ['label', 'message', 'toggle', 'tooltipRegion'];

  /** @type {HTMLElement|null} */
  #section = null;

  #requestId = 0;

  /** @type {string|null} */
  #tooltip = null;

  /** @type {AbortController|null} */
  #tooltipController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.refs.toggle.addEventListener('click', this.#handleToggleClick);
    this.#update({ sku: this.dataset.sku });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.refs.toggle?.removeEventListener('click', this.#handleToggleClick);
    this.#closeTooltip();
  }

  /**
   * @param {Event & { detail: { resource: { sku?: string }|null, data?: { productId?: string, newProduct?: { id: string } } } }} event
   */
  #handleVariantUpdate = (event) => {
    const { resource, data } = event.detail;

    if (data?.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data?.productId && data.productId !== this.dataset.productId) {
      return;
    }

    this.#update(resource);
  };

  /**
   * @param {{ sku?: string }|null} variant - The selected variant, null when the option combination does not exist
   */
  async #update(variant) {
    const requestId = ++this.#requestId;

    if (!variant) {
      this.#render(null);
      return;
    }

    this.hidden = false;
    const stockStatus = await fetchStockStatus(variant, this.dataset.productHandle);

    // A newer variant was selected while this request was pending
    if (requestId !== this.#requestId) return;

    this.#render(stockStatus);
  }

  /**
   * @param {import('./bbd-stock-status-v2.js').StockStatus|null} stockStatus
   */
  #render(stockStatus) {
    const online = stockStatus?.online;

    this.#closeTooltip();
    this.#tooltip = online?.message ? online.tooltip ?? null : null;
    this.refs.toggle.hidden = !this.#tooltip;

    SEVERITIES.forEach((severity) => this.classList.remove(`bbd-online-stock-status--${severity}`));

    if (!online?.message) {
      this.hidden = true;
      this.refs.label.textContent = '';
      this.refs.message.textContent = '';
      this.refs.message.hidden = true;
      delete this.dataset.status;
      return;
    }

    const [label, ...lines] = online.message.split('\n');
    this.refs.label.textContent = label;
    this.refs.message.textContent = lines.join('\n');
    this.refs.message.hidden = lines.length === 0;
    this.dataset.status = stockStatus.status;

    if (SEVERITIES.includes(online.severity)) {
      this.classList.add(`bbd-online-stock-status--${online.severity}`);
    }
  }

  #handleToggleClick = () => {
    if (this.#tooltipController) {
      this.#closeTooltip();
    } else {
      this.#openTooltip();
    }
  };

  /**
   * Toggletip: the text is inserted into a live region on open, so screen readers announce it.
   */
  #openTooltip() {
    if (!this.#tooltip) return;

    const bubble = document.createElement('span');
    bubble.className = 'bbd-online-stock-status__tooltip';
    bubble.textContent = this.#tooltip;
    this.refs.tooltipRegion.replaceChildren(bubble);
    this.refs.toggle.setAttribute('aria-expanded', 'true');

    this.#tooltipController = new AbortController();
    const { signal } = this.#tooltipController;

    document.addEventListener(
      'click',
      (event) => {
        if (!this.contains(/** @type {Node} */ (event.target))) this.#closeTooltip();
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        this.#closeTooltip();
        this.refs.toggle.focus();
      },
      { signal }
    );
  }

  #closeTooltip() {
    this.#tooltipController?.abort();
    this.#tooltipController = null;
    this.refs.tooltipRegion?.replaceChildren();
    this.refs.toggle?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('bbd-online-stock-status')) {
  customElements.define('bbd-online-stock-status', BbdOnlineStockStatus);
}
